import { useCallback, useEffect, useMemo, useState } from 'react'
import { usePrepareMeeting } from './usePrepareMeeting'
import { useBrowserDevices } from './useBrowserDevices'
import { useBrowserAudioMonitor } from './useBrowserAudioMonitor'
import useBrowserVideoPreview from './useBrowserVideoPreview'
import { useJoinMeeting } from './useJoinMeeting'
import type { PreviewState } from '@models/meeting-hooks'

export function usePreview({ appointmentId }: { appointmentId: string }) {
  const { ensureMeeting, isPreparing } = usePrepareMeeting()
  const [isRefreshing, setIsRefreshing] = useState(true)

  // Browser-native device management
  const deviceSelection = useBrowserDevices()

  // Track preview video state
  const [isVideoPreviewEnabled, setIsVideoPreviewEnabled] = useState(true)
  const [isMuted, setIsMuted] = useState(false)

  // Browser-native video preview
  const {
    stream: videoStream,
    error: browserVideoError,
    isLoading: isVideoLoading
  } = useBrowserVideoPreview({
    videoDeviceId: deviceSelection.selectedVideoInput,
    isVideoEnabled: isVideoPreviewEnabled && !!deviceSelection.selectedVideoInput
  })

  // Browser-native audio monitoring
  const audioLevel = useBrowserAudioMonitor({
    audioDeviceId: deviceSelection.selectedAudioInput,
    isEnabled: !!deviceSelection.selectedAudioInput,
    isMuted
  })

  const { join, isJoining, error } = useJoinMeeting(appointmentId)

  // Fetch appointment data on mount
  useEffect(() => {
    let mounted = true

    if (!appointmentId) {
      setIsRefreshing(false)
      return () => {
        mounted = false
      }
    }

    const preparePreview = async () => {
      setIsRefreshing(true)
      try {
        await ensureMeeting({ appointmentId, topic: null })
      } catch (prepareError) {
        if (!mounted) return

        const message = prepareError instanceof Error ? prepareError.message : 'Impossible de préparer le meeting.'

        console.error('[usePreview] Preview preparation failed:', {
          message,
          error: prepareError,
          appointmentId
        })
      } finally {
        if (mounted) {
          setIsRefreshing(false)
        }
      }
    }

    void preparePreview()

    return () => {
      mounted = false
    }
  }, [appointmentId])

  const toggleVideoPreview = useCallback(async () => {
    setIsVideoPreviewEnabled((prev) => !prev)
  }, [])

  const toggleMute = useCallback(() => {
    setIsMuted((prev) => !prev)
  }, [])

  const preview = useMemo<PreviewState>(
    () => ({
      audioInputs: deviceSelection.audioInputs,
      audioOutputs: deviceSelection.audioOutputs,
      videoInputs: deviceSelection.videoInputs,
      selectedAudioInput: deviceSelection.selectedAudioInput,
      selectedAudioOutput: deviceSelection.selectedAudioOutput,
      selectedVideoInput: deviceSelection.selectedVideoInput,
      isVideoPreviewActive: isVideoPreviewEnabled && !!videoStream,
      audioLevel,
      isReady: !deviceSelection.isRefreshing && !isRefreshing,
      audioError: deviceSelection.audioError,
      videoError: deviceSelection.videoError || (browserVideoError?.message),
      toggleVideoPreview,
      selectAudioInput: deviceSelection.selectAudioInput,
      selectAudioOutput: deviceSelection.selectAudioOutput,
      selectVideoInput: deviceSelection.selectVideoInput
    }),
    [
      deviceSelection.audioInputs,
      deviceSelection.audioOutputs,
      deviceSelection.videoInputs,
      deviceSelection.selectedAudioInput,
      deviceSelection.selectedAudioOutput,
      deviceSelection.selectedVideoInput,
      deviceSelection.audioError,
      deviceSelection.videoError,
      deviceSelection.isRefreshing,
      deviceSelection.selectAudioInput,
      deviceSelection.selectAudioOutput,
      deviceSelection.selectVideoInput,
      isVideoPreviewEnabled,
      videoStream,
      audioLevel,
      isRefreshing,
      browserVideoError,
      toggleVideoPreview
    ]
  )

  return {
    refreshDevices: deviceSelection.refreshDevices,
    preview,
    join,
    isJoining,
    error,
    isPreparing: isPreparing || isRefreshing || deviceSelection.isRefreshing,
    // Expose browser-native stream for video preview component
    videoStream,
    isVideoLoading,
    // Expose mute state and toggle
    isMuted,
    toggleMute
  }
}
